import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalFooter, ModalHeader, Row, Col, Form, FormGroup, Input, ListGroup, ListGroupItem, UncontrolledTooltip } from 'reactstrap';
import React, { useEffect, useState } from "react";
import { getPrimaryButtonClass, getPrimaryTextClass } from "helpers/sf_utils";
import { deleteCampaignNotes, updateCampaignNotes } from 'store/notes/campaign_notes/actions';
import { useDispatch, useSelector } from 'react-redux';
import DeleteModal from '../DeleteModal';
import { NOTE_DELETE_STR } from 'common/sf_strings';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import orderBy from 'lodash/orderBy';

const CampaignNoteModal = ({ modalState, onSubmitClick, onCloseClick, textHeading }) => {

  const { campaign_notes, current_account } = useSelector((state) => {
    return {
      campaign_notes: state.campaignNotesReducer.campaign_notes,
      current_account: state.accounts.current_account,
    };
  });

  const getNotesGroupByCampaignId = (campaign_id) => {
    const allNotesGroups = groupByCampaignId();
    return allNotesGroups.find(subArray => subArray.some(item => item.campaign_id === campaign_id)) || [];
  }

  const groupByCampaignId = () => {
    const notes = {};
    campaign_notes.forEach((item) => {
      const campaign_id = item.campaign_id;
      if (!notes[campaign_id]) {
        notes[campaign_id] = [];
      }
      notes[campaign_id].push(item);
    });
    return Object.values(notes);
  }

  const [inputText, setInputText] = useState('');
  const [notes, setNotes] = useState(getNotesGroupByCampaignId(modalState?.campaign_id) || []);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [deleteNoteModal, setDeleteNoteModal] = useState({
    action: false,
    note: {},
  })

  const sortedNotes = orderBy(notes, ['created_at'], ['desc']);

  useEffect(() => {
    setNotes(getNotesGroupByCampaignId(modalState?.campaign_id) || []);
  }, [campaign_notes]);

  useEffect(() => {
    if (modalState?.notes) {
      setNotes(modalState.notes);
    }
  }, [modalState]);

  const dispatch = useDispatch();

  const onClickSubmitModalState = () => {
    if (inputText.trim() !== '') {
      if (editingIndex !== -1) {
        // Update existing note
        setNotes((prevNotes) => {
          const updatedNotes = [...prevNotes];
          updatedNotes[editingIndex] = { text: inputText, campaign_id: modalState.campaign_id };
          return updatedNotes;
        });
        setEditingIndex(-1); // Reset editing index after updating
        dispatch(updateCampaignNotes({ id: notes[editingIndex]?.id, text: inputText, campaign_id: modalState.campaign_id }))
      } else {
        // Add new note
        setNotes([...notes, { text: inputText, campaign_id: modalState.campaign_id }]);
        onSubmitClick({ text: inputText, campaign_id: modalState.campaign_id });
      }
      setInputText('');
    }
  };

  const onClickRemoveNote = (note_obj) => {
    setNotes((prevNotes) => prevNotes.filter((note, i) => note.id !== note_obj?.note?.id));
    dispatch(deleteCampaignNotes(note_obj?.note?.id));
    setDeleteNoteModal({ action: false, note: {} })
  };

  const onClickEditNote = (index) => {
    setEditingIndex(index);
    setInputText(notes[index]?.text);
  };

  return (
    <>
      <DeleteModal
        deleteModalState={deleteNoteModal}
        onDeleteClick={onClickRemoveNote}
        textMessage={NOTE_DELETE_STR}
        onCloseClick={() => setDeleteNoteModal({ action: false, note: {} })}
        cancelButton
      />
      <Modal isOpen={modalState.action} toggle={onCloseClick} size={'lg'} centered={true}>
        <ModalHeader>
          <div className="d-flex justify-content-center">
            <i className={`align-self-bottom label-title bx bx-notepad ${getPrimaryTextClass()} me-1`}></i>
            <span className="modal-title call-transcription-topic-heading">
              {textHeading}
            </span>
          </div>
          <button
            type="button"
            className="close"
            data-dismiss="modal"
            aria-label="Close"
            onClick={onCloseClick}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12} className="mx-auto">
              <ListGroup className="my-3" style={{maxHeight: "290px", overflowY: "scroll"}}>
                {sortedNotes?.map((note, index) => (
                  <ListGroupItem className='mb-3 border-top' key={index}>
                    {note?.text}
                    <div className="mt-2 d-flex justify-content-between border-top">
                      <div className="mt-2">
                        <span className='mx-2'>
                          <i className={`bx bx-user ${getPrimaryTextClass()} me-2`}></i>
                          {note?.admin_user?.first_name ? note?.admin_user?.first_name + ' ' + note?.admin_user?.last_name : note?.admin_user?.email}
                        </span>
                        <span className='mx-2'>
                          <i className={`bx bx-calendar ${getPrimaryTextClass()} me-2`}></i>
                          {note?.created_at ? format(new Date(utcToZonedTime(note?.created_at, current_account?.timezone)), `MM-dd-yyyy hh:mm a`, ) : ''}
                        </span>
                      </div>

                      <div className="mt-2">
                        <i className='mx-2'>
                          <span>
                            <i
                              id={`tooltip-campaign-delete-${index}`}
                              className={`mdi mdi-delete font-size-16 ${getPrimaryTextClass()} me-2`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setDeleteNoteModal({ action: true, note: { id: note.id } })}
                            ></i>
                          </span>
                          <UncontrolledTooltip placement="top" target={`tooltip-campaign-delete-${index}`}>
                            Delete Note
                          </UncontrolledTooltip>
                        {/* We were using this functionality to edit note but not using it for now. */}

                          {/* <span>
                            <i
                              id={`tooltip-campaign-edit-${index}`}
                              className={`bx bx-edit font-size-14 ${getPrimaryTextClass()} me-2`}
                              onClick={() => onClickEditNote(index)}
                            ></i>
                          </span>
                          <UncontrolledTooltip placement="top" target={`tooltip-campaign-edit-${index}`}>
                            Edit Note
                          </UncontrolledTooltip> */}
                        </i>
                      </div>
                    </div>
                  </ListGroupItem>
                ))}
              </ListGroup>
              <Form>
                <FormGroup>
                  <Input
                    type="textarea"
                    value={inputText}
                    onChange={e => setInputText(e.target.value)}
                    placeholder="Type to add new note"
                  />
                </FormGroup>
              </Form>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <button type="button" className={`btn ${getPrimaryButtonClass()} ms-2`} onClick={onClickSubmitModalState}>Add</button>
          <button type="button" className="btn btn-light ms-2" onClick={onCloseClick}>Cancel</button>
        </ModalFooter>
      </Modal>
    </>
  );
};

CampaignNoteModal.propTypes = {
  onCloseClick: PropTypes.func,
  onSubmitClick: PropTypes.func,
  modalState: PropTypes.any,
  textHeading: PropTypes.any,
  cancelButton: PropTypes.bool
};

export default CampaignNoteModal;
